import { formatStatusLabel } from "@/components/operations/operations-shared";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useGetProducts,
  useInventories,
  usePurchaseOrders,
  useStockChecks,
} from "@/hooks";
import { cn } from "@/lib/utils";
import { getPurchaseOrderTotal } from "@/utils/order-total";
import { ArrowDown, ArrowUp } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

type StockCheckFilter = "all" | "non_otp" | "otp";

// 2FA groupings that drive the Non-OTP / OTP filters.
const NON_OTP_TWO_FA = new Set([
  "none",
  "email_forwarding",
  "otp_booking",
]);
const OTP_TWO_FA = new Set([
  "otp_login",
  "otp_authenticator",
  "ah_to_book",
]);

// Days after which an account is considered overdue for a stock check.
const NON_OTP_OVERDUE_DAYS = 7;
const OTP_OVERDUE_DAYS = 30;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

type SortDirection = "asc" | "desc";

function formatBuyCpm(value: number | undefined): string {
  return value === undefined ? "-" : (value * 100).toFixed(5);
}

export default function StockCheckGuidePage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<StockCheckFilter>("all");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  const inventoriesQuery = useInventories({
    pagination: { limit: 500 },
    orderBy: { field: "updatedAt", direction: "desc" },
  });
  const purchaseOrdersQuery = usePurchaseOrders({
    pagination: { limit: 1000 },
    orderBy: { field: "createdAt", direction: "desc" },
  });
  const stockChecksQuery = useStockChecks({
    pagination: { limit: 1000 },
    orderBy: { field: "checkedAt", direction: "desc" },
  });
  const productsQuery = useGetProducts();

  const inventories = useMemo(
    () => inventoriesQuery.data?.pages.flat() ?? [],
    [inventoriesQuery.data],
  );
  const purchaseOrders = useMemo(
    () => purchaseOrdersQuery.data?.pages.flat() ?? [],
    [purchaseOrdersQuery.data],
  );
  const stockChecks = useMemo(
    () => stockChecksQuery.data?.pages.flat() ?? [],
    [stockChecksQuery.data],
  );
  const products = useMemo(
    () => productsQuery.data ?? [],
    [productsQuery.data],
  );

  const productCodeById = useMemo(() => {
    const map = new Map<string, string>();
    for (const product of products) {
      if (product.id && product.productCode) {
        map.set(product.id, product.productCode);
      }
    }
    return map;
  }, [products]);

  // Buy CPM per inventory: average cost from purchase orders, falling back to
  // the source deal snapshot's unit price (mirrors the Inventory list).
  const inventoryBuyCpmMap = useMemo(() => {
    const purchaseTotalsByInventoryId = new Map<
      string,
      { miles: number; totalAmount: number }
    >();

    for (const purchaseOrder of purchaseOrders) {
      if (!purchaseOrder.inventoryId) continue;
      const existingTotals = purchaseTotalsByInventoryId.get(
        purchaseOrder.inventoryId,
      ) ?? { miles: 0, totalAmount: 0 };

      purchaseTotalsByInventoryId.set(purchaseOrder.inventoryId, {
        miles: existingTotals.miles + purchaseOrder.amountBought,
        totalAmount:
          existingTotals.totalAmount +
          getPurchaseOrderTotal({
            buyPrice: purchaseOrder.buyPrice,
            taxCost: purchaseOrder.taxCost,
            totalAmount: purchaseOrder.totalAmount,
          }),
      });
    }

    return new Map(
      inventories.map((inventory) => {
        const purchaseTotals = purchaseTotalsByInventoryId.get(inventory.id);
        const buyCpm =
          purchaseTotals && purchaseTotals.miles > 0
            ? purchaseTotals.totalAmount / purchaseTotals.miles
            : inventory.sourceDealSnapshot?.unitPrice;

        return [inventory.id, buyCpm] as const;
      }),
    );
  }, [inventories, purchaseOrders]);

  // Earliest purchase-order date per inventory: how long the stock has been
  // sitting (today − first PO). The inventory record itself has no PO date, so
  // this is derived from the joined purchase orders.
  const earliestPoDateByInventoryId = useMemo(() => {
    const map = new Map<string, Date>();
    for (const purchaseOrder of purchaseOrders) {
      if (!purchaseOrder.inventoryId) continue;
      const createdAt = purchaseOrder.createdAt;
      if (!createdAt) continue;
      const existing = map.get(purchaseOrder.inventoryId);
      if (!existing || createdAt.getTime() < existing.getTime()) {
        map.set(purchaseOrder.inventoryId, createdAt);
      }
    }
    return map;
  }, [purchaseOrders]);

  // Most recent stock check per inventory (stock checks are ordered desc, so
  // the first one we see for an inventory is the latest).
  const latestStockCheckByInventoryId = useMemo(() => {
    const map = new Map<string, (typeof stockChecks)[number]>();
    for (const stockCheck of stockChecks) {
      if (!map.has(stockCheck.inventoryId)) {
        map.set(stockCheck.inventoryId, stockCheck);
      }
    }
    return map;
  }, [stockChecks]);

  const rows = useMemo(() => {
    const now = Date.now();

    const allRows = inventories.map((inventory) => {
      const buyCpm = inventoryBuyCpmMap.get(inventory.id);
      const latestStockCheck = latestStockCheckByInventoryId.get(inventory.id);
      const lastCheckedAt = latestStockCheck?.checkedAt;
      const available = inventory.availableMiles;
      const earliestPoDate = earliestPoDateByInventoryId.get(inventory.id);
      const daysInStock = earliestPoDate
        ? Math.floor((now - earliestPoDate.getTime()) / MS_PER_DAY)
        : undefined;

      return {
        inventory,
        productCode:
          productCodeById.get(inventory.productId) ||
          inventory.productName ||
          "-",
        username: inventory.username || "-",
        available,
        accountBalance: latestStockCheck?.reportedBalance,
        lastCheckedAt,
        daysInStock,
        buyCpm,
        remainingStockValue: (buyCpm ?? 0) * available,
      };
    });

    const filteredRows = allRows.filter((row) => {
      if (filter === "all") return true;

      const twoFaStatus = row.inventory.twoFaStatus;
      const checkedTime = row.lastCheckedAt?.getTime();

      if (filter === "non_otp") {
        if (!NON_OTP_TWO_FA.has(twoFaStatus)) return false;
        // Overdue: never checked, or last checked more than 7 days ago.
        return (
          checkedTime === undefined ||
          now - checkedTime > NON_OTP_OVERDUE_DAYS * MS_PER_DAY
        );
      }

      // OTP
      if (!OTP_TWO_FA.has(twoFaStatus)) return false;
      // Overdue: never checked, or last checked more than 30 days ago.
      return (
        checkedTime === undefined ||
        now - checkedTime > OTP_OVERDUE_DAYS * MS_PER_DAY
      );
    });

    const directionMultiplier = sortDirection === "asc" ? 1 : -1;
    filteredRows.sort(
      (left, right) =>
        (left.remainingStockValue - right.remainingStockValue) *
        directionMultiplier,
    );

    return filteredRows;
  }, [
    inventories,
    inventoryBuyCpmMap,
    earliestPoDateByInventoryId,
    latestStockCheckByInventoryId,
    productCodeById,
    filter,
    sortDirection,
  ]);

  const isLoading =
    inventoriesQuery.isLoading ||
    purchaseOrdersQuery.isLoading ||
    stockChecksQuery.isLoading;

  const goToInventory = (inventoryId: string) => {
    navigate(`/operations/inventory?inventoryId=${inventoryId}`);
  };

  return (
    <div className="container mx-auto flex max-w-7xl flex-col gap-6 px-4 py-8 sm:px-6 lg:px-8">
      <Card className="flex min-h-0 flex-1 flex-col">
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>Stock Check Guide</CardTitle>
            <CardDescription>
              Accounts prioritized by remaining stock value (Buy CPM ×
              Available). Use the Non-OTP and OTP filters to surface accounts
              overdue for a balance check. Select a row to open it in Inventory.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="flex min-h-0 flex-1 flex-col">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <Button
              variant={filter === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter("all")}
            >
              All
            </Button>
            <Button
              variant={filter === "non_otp" ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter("non_otp")}
            >
              Non-OTP
            </Button>
            <Button
              variant={filter === "otp" ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter("otp")}
            >
              OTP
            </Button>
          </div>
          <Table containerClassName={cn("overflow-auto min-h-0 flex-1")}>
            <TableHeader className="sticky top-0 z-10 bg-background">
              <TableRow>
                <TableHead>Product Code</TableHead>
                <TableHead>Username</TableHead>
                <TableHead className="text-right">Available</TableHead>
                <TableHead className="text-right">Days in Stock</TableHead>
                <TableHead className="text-right">Buy CPM (¢)</TableHead>
                <TableHead className="text-right">
                  <Button
                    variant="ghost"
                    onClick={() =>
                      setSortDirection((prev) =>
                        prev === "asc" ? "desc" : "asc",
                      )
                    }
                    className="h-auto p-0 font-semibold hover:bg-transparent"
                  >
                    Remaining Stock Value
                    {sortDirection === "asc" ? (
                      <ArrowUp className="ml-2 h-4 w-4" />
                    ) : (
                      <ArrowDown className="ml-2 h-4 w-4" />
                    )}
                  </Button>
                </TableHead>
                <TableHead className="text-right">Account Balance</TableHead>
                <TableHead>2FA Status</TableHead>
                <TableHead>Last Checked At</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    className="text-center text-muted-foreground"
                  >
                    Loading stock check guide...
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    className="text-center text-muted-foreground"
                  >
                    No accounts match this filter.
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                  <TableRow
                    key={row.inventory.id}
                    className="cursor-pointer"
                    onClick={() => goToInventory(row.inventory.id)}
                  >
                    <TableCell title={row.inventory.productName}>
                      {row.productCode}
                    </TableCell>
                    <TableCell>{row.username}</TableCell>
                    <TableCell className="text-right">
                      {row.available.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {row.daysInStock === undefined
                        ? "—"
                        : row.daysInStock.toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatBuyCpm(row.buyCpm)}
                    </TableCell>
                    <TableCell className="text-right">
                      {row.buyCpm === undefined
                        ? "-"
                        : row.remainingStockValue.toLocaleString(undefined, {
                            style: "currency",
                            currency: "USD",
                          })}
                    </TableCell>
                    <TableCell className="text-right">
                      {row.accountBalance === undefined
                        ? "-"
                        : row.accountBalance.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {formatStatusLabel(row.inventory.twoFaStatus)}
                    </TableCell>
                    <TableCell>
                      {row.lastCheckedAt
                        ? row.lastCheckedAt.toLocaleDateString()
                        : "-"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
